import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Box, Typography, Alert, CircularProgress, TextField,
  InputAdornment, IconButton, Tooltip, useMediaQuery, useTheme,
  Drawer, Fab, Button, Chip, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import PrintIcon from '@mui/icons-material/Print';
import PeopleIcon from '@mui/icons-material/People';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import UndoIcon from '@mui/icons-material/Undo';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import DialogHeader from '../Common/DialogHeader';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  pointerWithin,
  rectIntersection,
} from '@dnd-kit/core';

import { useSeatingState } from './Seating/useSeatingState';
import SeatingToolbar from './Seating/SeatingToolbar';
import FloorPlanCanvas from './Seating/FloorPlanCanvas';
import GuestListPanel from './Seating/GuestListPanel';
import TableDetailDialog from './Seating/TableDetailDialog';
import { ICON_MAP } from './Seating/ModulePalette';
import { ZOOM_MIN, ZOOM_MAX, GRID_SIZE, PALETTE_MODULES, MODULE_STYLES, getRsvpSuffix } from './Seating/constants';

const pulseStyle = `
  @keyframes seatingPulse {
    0%   { box-shadow: 0 0 0 0 rgba(244,162,97,0.7); }
    70%  { box-shadow: 0 0 0 12px rgba(244,162,97,0); }
    100% { box-shadow: 0 0 0 0 rgba(244,162,97,0); }
  }
`;

// A szülő Paper-eken lévő backdropFilter (frosted-glass dizájn) új "containing block"-ot hoz
// létre CSS szerint, ami eltéríti a position:fixed elemeket a valós viewporttól — ez törte el
// a CSS-alapú mobil fullscreen fallback-et. Portal-lal kikerüljük ezt: a fullscreen doboz
// közvetlenül a body alá kerül, teljesen függetlenül attól, mi történik a fejlécekben.
function MaybePortal({ active, children }) {
  return active ? createPortal(children, document.body) : children;
}

const SMALL_SCREEN_HINT_KEY = 'seatingSmallScreenHintDismissed';

// "Tóth Ödön" → "toth odon": a kereséshez ne kelljen pontosan eltalálni az ékezeteket
const normalizeForSearch = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export default function SeatingTab({ event, onRefresh, highlightedGuestId: externalHighlight }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Álló tájolásban (telefon ÉS tablet is) a normál oldalt-oldalt elrendezés helyett a
  // canvas fent, az ültethető személyek lent — álló módban a canvas mellett nem marad
  // elég szélesség egy oldalpanelnek. Telefon fekvő nézetben (ritka eset) marad a FAB+drawer.
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const isStackedLayout = isPortrait;

  const [smallScreenHintDismissed, setSmallScreenHintDismissed] = useState(() => {
    try { return localStorage.getItem(SMALL_SCREEN_HINT_KEY) === '1'; } catch { return false; }
  });
  const dismissSmallScreenHint = () => {
    setSmallScreenHintDismissed(true);
    try { localStorage.setItem(SMALL_SCREEN_HINT_KEY, '1'); } catch { /* privát mód */ }
  };

  const state = useSeatingState(event?.id);

  // Refs for pinch zoom calculations — avoids stale closure when RAF fires before React re-render.
  // Updated synchronously in handlePinchChange AND via useLayoutEffect after each commit.
  const pinchZoomRef = useRef(state.zoom);
  const pinchPanRef = useRef(state.pan);
  useLayoutEffect(() => {
    pinchZoomRef.current = state.zoom;
    pinchPanRef.current = state.pan;
  }); // intentionally no deps: runs after every commit, before paint

  const [selectedElementId, setSelectedElementId] = useState(null);
  const [tableDialogTableId, setTableDialogTableId] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cssFullscreen, setCssFullscreen] = useState(false);
  const containerRef = useRef(null);

  const effectiveFullscreen = isFullscreen || cssFullscreen;

  // Mért, tényleges elérhető magasság a konténer fölötti tartalom (fejléc / esemény-kártya /
  // tab-sáv) alapján — a korábbi fix "295px" feltételezés eltért a valós magasságtól, emiatt
  // az eszköztár és a fullscreen-gomb a képernyő alja alá csúszott mobilon/tableten.
  const [measuredHeight, setMeasuredHeight] = useState(null);
  useLayoutEffect(() => {
    if (effectiveFullscreen) return;
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      // Egy nyitott MUI Dialog/Modal a body-ra `overflow:hidden`-t (és scrollbar-
      // kompenzáló padding-right-ot) tesz, ami a legtöbb böngészőben egy "resize"
      // eseményt vált ki (a scrollbar eltűnése miatt megváltozik a viewport szélessége).
      // Ez a "resize" újra lefuttatná ezt a mérést egy MÁSIK (azóta esetleg elgörgetett)
      // scrollY mellett, mint amikor a konténer eredetileg mérve lett — emiatt a canvas
      // magassága hirtelen "megnőtt" (vagy zsugorodott) egy dialógus megnyitásakor
      // (pl. törlés megerősítése), holott a tényleges elérhető hely nem változott.
      // Egy nyitott dialógus alatti resize-t ezért figyelmen kívül hagyunk — a dialógus
      // bezárásakor (ami maga is resize-t vált ki) úgyis frissen újramérünk.
      if (document.body.style.overflow === 'hidden') return;
      // A konténer dokumentumhoz viszonyított (scroll-független) tetejét használjuk. A
      // viewporthoz viszonyított top az aktuális scrollY-tól függ, így minden újramérés
      // (pl. dialógus nyitás/zárás után) más magasságot adott, a nagyobb konténer pedig
      // még több scrollt tett lehetővé — ez a vászon "végtelen" növekedése volt.
      const top = el.getBoundingClientRect().top + window.scrollY;
      const vh = window.visualViewport?.height ?? window.innerHeight;
      // Felső korlát: böngésző-zoom kicsinyítéskor (Ctrl+görgő) a viewport CSS-pixelben
      // nagyobb lesz, de a munkafelület ne nőjön vele a végtelenségig.
      const minH = isMobile ? 380 : 500;
      const maxH = isMobile ? 700 : 640;
      setMeasuredHeight(Math.min(maxH, Math.max(minH, Math.round(vh - top - 12))));
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
    // state.loading is included so this re-runs once the canvas actually mounts
    // (containerRef.current is null during the loading-spinner render, and refs
    // aren't tracked as effect dependencies, so without this the effect would
    // never re-fire once the real container appears).
  }, [effectiveFullscreen, isMobile, state.loading, smallScreenHintDismissed]);

  const toggleFullscreen = () => {
    if (cssFullscreen) {
      setCssFullscreen(false);
      return;
    }
    if (document.fullscreenEnabled) {
      if (!document.fullscreenElement) {
        containerRef.current?.requestFullscreen().catch(() => {
          setCssFullscreen(true);
        });
      } else {
        document.exitFullscreen();
      }
    } else {
      setCssFullscreen(prev => !prev);
    }
  };

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);
  const [guestDrawerOpen, setGuestDrawerOpen] = useState(false);
  const [guestPanelCollapsed, setGuestPanelCollapsed] = useState(false);
  const [guestSearch, setGuestSearch] = useState('');
  const [polygonEditMode, setPolygonEditMode] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [deleteConfirmState, setDeleteConfirmState] = useState({ open: false, id: null, isTable: false, label: '' });

  const handleDeleteRequest = useCallback((id, isTable) => {
    let label;
    if (isTable) {
      label = state.seatingChart.tables?.find(t => t.id === id)?.tableName ?? `Asztal #${id}`;
    } else {
      const el = state.layout.elements.find(el => el.id === id);
      label = el?.label || MODULE_STYLES[el?.type]?.label || 'Elem';
    }
    setDeleteConfirmState({ open: true, id, isTable, label });
  }, [state]);

  const handleTogglePolygonEdit = useCallback(() => {
    setPolygonEditMode(prev => {
      if (!prev && !state.layout.venue.polygon) {
        // Initialise with a rectangle matching the current venue size
        const { width, height } = state.layout.venue;
        state.setVenuePolygon([
          { x: 0, y: 0 },
          { x: width, y: 0 },
          { x: width, y: height },
          { x: 0, y: height },
        ]);
      }
      return !prev;
    });
  }, [state]);

  const handleClearPolygon = useCallback(() => {
    state.setVenuePolygon(null);
    setPolygonEditMode(false);
  }, [state]);

  // ─── Ctrl+Z undo ────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        state.undo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state]);

  // canvasRef is created here and passed down to FloorPlanCanvas
  const canvasRef = useRef(null);

  // ─── Palette item click → place at canvas center ────────────────────────────
  const handlePaletteItemClick = useCallback((moduleType, isTable) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const canvasX = (rect.width / 2 - state.pan.x) / state.zoom;
    const canvasY = (rect.height / 2 - state.pan.y) / state.zoom;
    if (isTable) {
      state.createTableOnCanvas(moduleType, canvasX, canvasY);
    } else {
      state.createModuleOnCanvas(moduleType, canvasX, canvasY);
    }
  }, [canvasRef, state]);

  // ─── DnD setup (at this level so toolbar palette + canvas + guest panel share context) ──
  const [activeDragData, setActiveDragData] = useState(null);
  const [dragStartClient, setDragStartClient] = useState({ x: 0, y: 0 });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } })
  );

  // Custom collision detection:
  // - prefer trash zone over canvas when the pointer is directly over it (default
  //   rectIntersection always picks the canvas — full-screen droppable — over the
  //   smaller trash zone).
  // - when dragging a GUEST, prefer a specific seat droppable ("seat-...") over the
  //   whole-table droppable when the pointer is exactly over a seat, so precise seat
  //   targeting wins over the table-wide auto-assign fallback.
  const collisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.some(c => c.id === 'trash')) {
      return pointerCollisions.filter(c => c.id === 'trash');
    }
    if (activeDragData?.type === 'GUEST') {
      const seatCollisions = pointerCollisions.filter(c => String(c.id).startsWith('seat-'));
      if (seatCollisions.length > 0) return seatCollisions;
    }
    return rectIntersection(args);
  }, [activeDragData]);

  const handleDragStart = useCallback(({ active, activatorEvent }) => {
    setActiveDragData(active.data.current ?? null);
    setDragStartClient({
      x: activatorEvent?.clientX ?? 0,
      y: activatorEvent?.clientY ?? 0,
    });
  }, []);

  const handleDragEnd = useCallback(({ active, delta, over }) => {
    const data = active.data.current;
    setActiveDragData(null);
    if (!data) return;

    // 1. Palette item → canvas
    if (data.type === 'PALETTE_ITEM') {
      if (!over || over.id !== 'canvas') return;
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const dropClientX = dragStartClient.x + delta.x;
      const dropClientY = dragStartClient.y + delta.y;
      const rawX = (dropClientX - rect.left - state.pan.x) / state.zoom;
      const rawY = (dropClientY - rect.top  - state.pan.y) / state.zoom;
      const snappedX = Math.round(rawX / GRID_SIZE) * GRID_SIZE;
      const snappedY = Math.round(rawY / GRID_SIZE) * GRID_SIZE;
      if (data.isTable) {
        state.createTableOnCanvas(data.moduleType, snappedX, snappedY);
      } else {
        state.createModuleOnCanvas(data.moduleType, snappedX, snappedY);
      }
      return;
    }

    // 2. Canvas element repositioned or dropped on trash
    if (data.type === 'CANVAS_ELEMENT') {
      if (over?.id === 'trash') {
        handleDeleteRequest(data.id, data.isTable);
        return;
      }
      const dx = delta.x / state.zoom;
      const dy = delta.y / state.zoom;
      state.moveElement(data.id, dx, dy, data.isTable);
      return;
    }

    // 3. Guest dropped onto a specific seat, or somewhere on a table (auto-assign)
    if (data.type === 'GUEST') {
      if (!over) return;
      const overData = over.data?.current;
      const guestId = data.guestId;
      const assign = (tableId, seatNumber) => {
        if (String(guestId).startsWith('companion-')) {
          state.assignCompanionToSeat(guestId, tableId, seatNumber);
        } else {
          state.assignGuestToSeat(guestId, tableId, seatNumber);
        }
      };
      if (overData?.type === 'SEAT') {
        assign(overData.tableId, overData.seatNumber);
      } else if (overData?.type === 'CANVAS_ELEMENT' && overData.isTable) {
        assign(overData.id, null);
      }
      return;
    }
  }, [dragStartClient, state, handleDeleteRequest]);

  // ─── External highlight (GuestsTab → jump to seat) ─────────────────────────
  useEffect(() => {
    if (!externalHighlight) return;
    // Nincs zoom/pásztázás: a teljes vászon látszik, csak a szék villog.
    state.setHighlightedGuestId(externalHighlight);
    const t = setTimeout(() => state.setHighlightedGuestId(null), 4000);
    return () => clearTimeout(t);
  }, [externalHighlight]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Zoom handler (called by canvas wheel + toolbar buttons) ───────────────
  const handleZoomChange = useCallback(({ delta, pivotX, pivotY }) => {
    const nextZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, state.zoom * delta));
    if (pivotX !== undefined) {
      state.setPan({
        x: pivotX - (pivotX - state.pan.x) * (nextZoom / state.zoom),
        y: pivotY - (pivotY - state.pan.y) * (nextZoom / state.zoom),
      });
    }
    state.setZoom(nextZoom);
  }, [state]);

  const handleZoomReset = useCallback(() => {
    state.setZoom(1);
    state.setPan({ x: 20, y: 20 });
  }, [state]);

  // Combined zoom+pan for touch pinch gesture (pivot at pinch midpoint + midpoint translation).
  // Reads from refs (not state) so rapid RAF callbacks always have the latest values,
  // even if React hasn't re-rendered yet. Refs are updated synchronously here and
  // synced from state via useLayoutEffect after each commit.
  const handlePinchChange = useCallback(({ delta, pivotX, pivotY, midDeltaX, midDeltaY }) => {
    const currentZoom = pinchZoomRef.current;
    const currentPan = pinchPanRef.current;
    const nextZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, currentZoom * delta));
    const nextPan = {
      x: pivotX - (pivotX - currentPan.x) * (nextZoom / currentZoom) + midDeltaX,
      y: pivotY - (pivotY - currentPan.y) * (nextZoom / currentZoom) + midDeltaY,
    };
    // Update refs immediately so the next RAF call has fresh values before React re-renders
    pinchZoomRef.current = nextZoom;
    pinchPanRef.current = nextPan;
    state.setPan(nextPan);
    state.setZoom(nextZoom);
  }, [state]); // state dep kept for setPan/setZoom stability

  // Fit the entire venue into the visible canvas area
  const handleFitToScreen = useCallback(() => {
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const { width: venueW, height: venueH } = state.layout.venue;
    const padding = 24;
    const nextZoom = Math.min(
      Math.max(Math.min((rect.width - padding * 2) / venueW, (rect.height - padding * 2) / venueH), ZOOM_MIN),
      ZOOM_MAX,
    );
    state.setZoom(nextZoom);
    state.setPan({
      x: (rect.width - venueW * nextZoom) / 2,
      y: (rect.height - venueH * nextZoom) / 2,
    });
  }, [state, canvasRef]);

  // ─── Guest search ──────────────────────────────────────────────────────────
  // Ékezet- és kisbetű-független, szavanként előtag-egyezés: a "toth an" megtalálja
  // "Tóth Anna"-t, a "kiss" minden Kisst. Korábban csak pontosan EGY, ékezetre is pontos
  // találatnál történt bármi, így a felhasználó szinte mindig azt látta, hogy "nem csinál semmit".
  const searchCandidates = [
    ...state.allGuests.map(g => ({ id: g.id, name: `${g.lastName || ''} ${g.firstName || ''}`.trim() })),
    // Kísérők: a vásznon `companion-<vendégId>` azonosítóval ülnek
    ...state.allGuests
      .filter(g => g.plusOne && g.plusOneName)
      .map(g => ({ id: `companion-${g.id}`, name: g.plusOneName, companionOf: `${g.lastName || ''} ${g.firstName || ''}`.trim() })),
  ];
  const findGuestMatches = (value) => {
    const tokens = normalizeForSearch(value).split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return [];
    return searchCandidates.filter(c => {
      const words = normalizeForSearch(c.name).split(/\s+/);
      return tokens.every(t => words.some(w => w.startsWith(t)));
    });
  };

  const handleGuestSearch = (value) => {
    setGuestSearch(value);
    const matches = findGuestMatches(value);
    // Minden találat széke kiemelve marad, amíg a keresőben van szöveg (nem tűnik el 4 mp után).
    // Nem zoomolunk/mozgatjuk a nézetet: a teljes vászon látszik.
    state.setHighlightedGuestId(matches.length ? matches.map(m => m.id) : null);
  };

  const clearGuestSearch = () => {
    setGuestSearch('');
    state.setHighlightedGuestId(null);
  };

  // Enrich ALL tables with companion guests from layout.companionSeats.
  // This makes companions visible on the canvas (seat dots + counter) and in the dialog.
  const companionEntries = Object.entries(state.layout.companionSeats ?? {});
  const enrichedSeatingChart = {
    ...state.seatingChart,
    tables: (state.seatingChart.tables ?? []).map(table => {
      const companions = companionEntries
        .filter(([, v]) => v.tableId === table.id)
        .map(([companionId, v]) => {
          const parentId = parseInt(companionId.replace('companion-', ''), 10);
          const parent = state.allGuests.find(g => g.id === parentId);
          return {
            id: companionId,
            firstName: parent?.plusOneName ?? '?',
            lastName: '',
            isCompanion: true,
            seatNumber: v.seatNumber ?? null,
            rsvpStatus: parent?.rsvpStatus,
            guestCategory: (parent?.guestCategory === 'bride' || parent?.guestCategory === 'groom')
              ? parent.guestCategory : null,
          };
        });
      if (companions.length === 0) return table;
      return { ...table, guests: [...(table.guests ?? []), ...companions] };
    }),
  };

  // A „Ki hol ül?" kereső találati listája: kinek melyik asztal jutott (vagy még senki)
  const guestSearchResults = guestSearch.trim()
    ? findGuestMatches(guestSearch).map(m => ({
        ...m,
        tableName: (enrichedSeatingChart.tables ?? [])
          .find(t => t.guests?.some(g => g.id === m.id))?.tableName ?? null,
      }))
    : [];

  const tableDialogDbTable = tableDialogTableId
    ? (enrichedSeatingChart.tables ?? []).find(t => t.id === tableDialogTableId)
    : null;

  // Dialog already gets an enriched table from enrichedSeatingChart above
  const tableDialogDbTableEnriched = tableDialogDbTable ?? null;

  // ─── Toolbox — mindig egy vízszintes sáv a canvas fölött, minden nézetben (mobil, tablet,
  // desktop). Álló mobil/tablet nézetben (stacked) a helyszín méret/alak + szín szűrő
  // beállítások a fogaskerék gombja mögötti dialógusban élnek (kevés a hely); fekvő/desktop
  // nézetben ugyanez a sáv mutatja a méret/alak mezőket is, mert ott elfér. ────────────────
  const toolbarContent = (
    <SeatingToolbar
      venue={state.layout.venue}
      onVenueSizeChange={(w, h) => state.setVenueSize(w, h)}
      onSetPolygon={(polygon) => {
        state.setVenuePolygon(polygon);
        setPolygonEditMode(false);
      }}
      activeFilter={state.activeFilter}
      onFilterChange={state.setActiveFilter}
      isDirty={state.isDirty}
      onZoomReset={handleZoomReset}
      polygonEditMode={polygonEditMode}
      onTogglePolygonEdit={handleTogglePolygonEdit}
      onClearPolygon={handleClearPolygon}
      onPaletteItemClick={handlePaletteItemClick}
      stacked={isStackedLayout}
      toolboxIconSize={isMobile ? 36 : 44}
    />
  );

  const allUnassigned = [
    ...(state.seatingChart.unassignedGuests ?? []),
    ...state.unassignedCompanions,
  ];

  const totalCompanions = state.allGuests.filter(g => g.plusOne && g.plusOneName).length;

  const guestPanel = (
    <GuestListPanel
      unassignedGuests={allUnassigned}
      allGuests={state.allGuests}
      totalCompanions={totalCompanions}
      tables={enrichedSeatingChart.tables ?? []}
      activeFilter={state.activeFilter}
      onClose={isMobile && !isStackedLayout ? () => setGuestDrawerOpen(false) : undefined}
      stacked={isStackedLayout}
      gridColumns={isMobile ? 2 : 3}
      collapsed={isStackedLayout ? guestPanelCollapsed : false}
      onToggleCollapse={isStackedLayout ? () => setGuestPanelCollapsed(c => !c) : undefined}
    />
  );

  // ─── Loading / error ───────────────────────────────────────────────────────
  if (state.loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Ültetési rend betöltése...</Typography>
      </Box>
    );
  }

  if (state.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button size="small" onClick={state.fetchAll}>Újra</Button>}>
          {state.error}
        </Alert>
      </Box>
    );
  }

  // ─── Active drag overlay content ───────────────────────────────────────────
  // Paletta-elemnél magát az ikont mutatjuk (nem szöveges címkét), pontosan akkora
  // dobozban, mint az eredeti toolbox-ikon volt (activeDragData.size) — így a kurzor
  // a húzott elem közepén marad, nem tolódik el mellette egy betű/felirat.
  const activePaletteModule = activeDragData?.type === 'PALETTE_ITEM'
    ? PALETTE_MODULES.find(m => m.type === activeDragData.moduleType)
    : null;

  const overlayGuestLabel = activeDragData?.type === 'GUEST'
    ? (() => {
        const g = [...(state.seatingChart.unassignedGuests ?? []), ...state.allGuests]
          .find(g => g.id === activeDragData.guestId);
        return g ? `${g.lastName} ${g.firstName}` : 'Vendég';
      })()
    : null;

  return (
    <>
      <style>{pulseStyle}</style>

      {/* Telefonon a vászon túl kicsi a kényelmes szerkesztéshez — jelezzük, hol jobb */}
      {isMobile && !effectiveFullscreen && !smallScreenHintDismissed && (
        <Alert
          severity="info"
          onClose={dismissSmallScreenHint}
          sx={{ mb: 1.5, borderRadius: 2 }}
        >
          Az ültetési rend tabletről vagy számítógépről használható teljes élménnyel.
          Telefonon a vendégek ültetése működik, de a terem tervezéséhez nagyobb képernyő ajánlott.
        </Alert>
      )}

      {/*
        DndContext wraps the entire layout so that:
        - ModulePalette draggables (in SeatingToolbar)
        - GuestListPanel draggables
        - Canvas elements (TableElement, ModuleElement)
        all share the same drag context.
      */}
      <DndContext autoScroll={false} sensors={sensors} collisionDetection={collisionDetection} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <MaybePortal active={cssFullscreen}>
        <Box
          ref={containerRef}
          className="seating-interactive"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            // Amíg az első mérés lefut, a régi becsléssel dolgozunk, hogy ne legyen layout-ugrás.
            height: measuredHeight ?? { xs: 'calc(100svh - 295px)', md: 'calc(100vh - 200px)' },
            minHeight: { xs: 380, sm: 500 },
            overflow: 'hidden',
            ...(effectiveFullscreen && {
              height: '100vh',
              minHeight: '100vh',
              bgcolor: 'background.paper',
            }),
            ...(cssFullscreen && {
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100dvh',
              zIndex: 1300,
            }),
          }}
        >

          {/* Top bar */}
          <Box
            className="no-print"
            sx={{
              px: { xs: 1, md: 2 }, py: 0.75,
              borderBottom: '1px solid', borderColor: 'divider',
              display: 'flex', alignItems: 'center', gap: 0.75,
              bgcolor: '#fafaf7', flexShrink: 0,
              // Telefonon a kereső saját sort kap, különben az ikonok kicsúsznak / a mező olvashatatlanul keskeny
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              justifyContent: isMobile ? 'space-between' : undefined,
            }}
          >
            {/* A teremben ültetett vendéget keresi és kiemeli — a jobb oldali panel szűrője ettől független */}
            <Box sx={{ position: 'relative', flex: isMobile ? '1 1 100%' : 1, minWidth: 130, maxWidth: { md: 280 } }}>
              <TextField
                placeholder="Ki hol ül?"
                value={guestSearch}
                onChange={e => handleGuestSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') clearGuestSearch(); }}
                size="small"
                fullWidth
                inputProps={{ 'aria-label': 'Ki hol ül? Vendég keresése a teremben' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment>,
                  endAdornment: guestSearch ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={clearGuestSearch} aria-label="Keresés törlése" edge="end">
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              {/* Találati lista: a keresés mindig ad visszajelzést (hol ül / még nincs leültetve / nincs ilyen) */}
              {guestSearch.trim() && (
                <Paper
                  elevation={4}
                  sx={{
                    position: 'absolute', top: '100%', left: 0, mt: 0.5, zIndex: 20,
                    width: { xs: 'min(320px, calc(100vw - 32px))', sm: 320 },
                    maxHeight: 260, overflowY: 'auto', borderRadius: 2,
                  }}
                >
                  {guestSearchResults.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                      Nincs ilyen nevű vendég
                    </Typography>
                  ) : (
                    guestSearchResults.slice(0, 20).map(r => (
                      <Box
                        key={r.id}
                        component="button"
                        type="button"
                        onClick={() => state.setHighlightedGuestId([r.id])}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 1, width: '100%',
                          px: 2, py: 1, border: 0, bgcolor: 'transparent', textAlign: 'left',
                          cursor: 'pointer', font: 'inherit',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>{r.name}</Typography>
                          {r.companionOf && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                              {r.companionOf} kísérője
                            </Typography>
                          )}
                        </Box>
                        {r.tableName ? (
                          <Chip
                            icon={<EventSeatIcon sx={{ fontSize: 14 }} />}
                            label={r.tableName}
                            size="small"
                            sx={{ bgcolor: '#fde8d7', color: '#8a4b14', fontWeight: 600, maxWidth: 140 }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            még nincs leültetve
                          </Typography>
                        )}
                      </Box>
                    ))
                  )}
                </Paper>
              )}
            </Box>
            {!isMobile && <Box sx={{ flex: 1 }} />}
            <Tooltip title="Visszavonás (Ctrl+Z)">
              <IconButton size="small" onClick={state.undo}>
                <UndoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Előlről kezdés">
              <IconButton size="small" color="error" onClick={() => setResetDialogOpen(true)}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isMobile ? (
              <>
                <Tooltip title="Kicsinyítés">
                  <IconButton size="small" onClick={() => handleZoomChange({ delta: 0.85 })}>
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Nagyítás">
                  <IconButton size="small" onClick={() => handleZoomChange({ delta: 1.15 })}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Illeszd a képernyőre">
                  <IconButton size="small" onClick={handleFitToScreen}>
                    <CenterFocusStrongIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            ) : (
              <>
                <Tooltip title="Kicsinyítés">
                  <IconButton size="small" onClick={() => handleZoomChange({ delta: 0.9 })}>
                    <ZoomOutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="caption" sx={{ minWidth: 38, textAlign: 'center' }}>
                  {Math.round(state.zoom * 100)}%
                </Typography>
                <Tooltip title="Nagyítás">
                  <IconButton size="small" onClick={() => handleZoomChange({ delta: 1.1 })}>
                    <ZoomInIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Nézet visszaállítása">
                  <IconButton size="small" onClick={handleZoomReset}>
                    <CenterFocusStrongIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title="Nyomtatás / PDF">
              <IconButton size="small" onClick={() => {
                const src = document.querySelector('.seating-print-view');
                if (!src) return;
                const clone = src.cloneNode(true);
                clone.style.display = 'block';
                clone.classList.add('seating-print-active');
                document.body.appendChild(clone);
                document.body.classList.add('seating-print-mode');
                window.print();
                document.body.classList.remove('seating-print-mode');
                document.body.removeChild(clone);
              }}>
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={effectiveFullscreen ? 'Kilépés a teljes képernyőből' : 'Teljes képernyős mód'}>
              <IconButton size="small" onClick={toggleFullscreen} color={effectiveFullscreen ? 'primary' : 'default'}>
                {effectiveFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>

          {/* Toolbox — mindig egy teljes szélességű sáv a canvas fölött, minden nézetben. */}
          <Box sx={{ flexShrink: 0, overflow: 'hidden' }}>
            {toolbarContent}
          </Box>

          {/* Canvas + vendéglista — álló tájolásban (telefon és tablet is) oszlop-elrendezésre
              váltunk: a canvas fent, az ültethető személyek lent, fix, korlátozott magasságú
              saját görgetéssel, hogy ne "végtelenbe" görgethető legyen (a flex:1 + minHeight:0
              hiánya Safari/iOS-en elszabaduló magasságot okozott). */}
          <Box sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: isStackedLayout ? 'column' : 'row',
            overflow: 'hidden',
          }}>
            <FloorPlanCanvas
              canvasRef={canvasRef}
              layout={state.layout}
              seatingChart={enrichedSeatingChart}
              zoom={state.zoom}
              pan={state.pan}
              onZoomChange={handleZoomChange}
              onPanChange={state.setPan}
              onPinchChange={handlePinchChange}
              activeFilter={state.activeFilter}
              activeDragData={activeDragData}
              selectedElementId={selectedElementId}
              onSelectElement={setSelectedElementId}
              highlightedGuestId={state.highlightedGuestId}
              onResizeElement={(id, x, y, w, h, isTable) => state.resizeElement(id, x, y, w, h, isTable)}
              onDeleteElement={handleDeleteRequest}
              onRenameTable={state.renameTable}
              onTableClick={(tableId) => setTableDialogTableId(tableId)}
              onRotateElement={(id, angle, isTable) => state.updateRotation(id, angle, isTable)}
              polygonEditMode={polygonEditMode}
              onPolygonChange={state.setVenuePolygon}
            />

            {isStackedLayout ? (
              <Box sx={{
                height: guestPanelCollapsed ? 'auto' : (isMobile ? 220 : 260),
                flexShrink: 0,
                overflow: 'hidden',
                transition: 'height 0.15s ease',
              }}>
                {guestPanel}
              </Box>
            ) : (
              !isMobile && guestPanel
            )}
          </Box>
        </Box>
        </MaybePortal>

        {/* DragOverlay – position:fixed, ezért body alá kell portálozni: ugyanaz a
            "backdrop-filter új containing blockot csinál" hiba, ami a fullscreen fallbacket
            és a mobil FAB-okat is érintette — enélkül a szülő (frosted-glass) Paper dobozához
            igazodna a fixed pozíció a valós viewport helyett, és az ikon messze a kurzortól
            jelenne meg. */}
        {createPortal(
        <DragOverlay dropAnimation={null}>
          {activePaletteModule && (() => {
            const size = activeDragData.size ?? 44;
            const style_ = MODULE_STYLES[activePaletteModule.type] || { bgcolor: '#e0e0e0', color: '#616161' };
            const iconFontSize = Math.round(size * 0.45);
            return (
              <Box sx={{
                width: size,
                height: size,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#fff',
                border: '2px solid #2a5d32',
                borderRadius: 1.5,
                boxShadow: '0 4px 14px rgba(0,0,0,0.28)',
                cursor: 'grabbing',
                pointerEvents: 'none',
                color: style_.color,
              }}>
                {ICON_MAP[activePaletteModule.type]
                  ? ICON_MAP[activePaletteModule.type](iconFontSize)
                  : <span>{activePaletteModule.label[0]}</span>}
              </Box>
            );
          })()}
          {overlayGuestLabel && (
            <Chip
              label={overlayGuestLabel}
              sx={{ bgcolor: '#2a5d32', color: '#fff', cursor: 'grabbing', pointerEvents: 'none' }}
            />
          )}
        </DragOverlay>,
        document.body
        )}
      </DndContext>

      {/* Mobile FAB (outside DndContext to avoid interaction). Csak telefon FEKVŐ nézetben
          kell, mert álló nézetben (isStackedLayout) a vendéglista már állandóan lent látszik.
          Portal-ozva a body alá: a szülő Paper backdropFilter-je miatt a position:fixed
          FAB-ok az ő dobozához, nem a valós viewporthoz igazodnának (ugyanaz a "containing
          block" probléma, ami a fullscreen fallback-et is érintette), és a magassága a
          fullscreen vászon portal-mozgatásakor is változik. zIndex 1400, hogy fullscreen
          alatt is a vászon (1300) fölött maradjanak. */}
      {isMobile && !isStackedLayout && createPortal(
        <>
          <Fab variant="extended" size="small"
            sx={{ position: 'fixed', bottom: 80, right: 12, bgcolor: '#2a5d32', color: '#fff', zIndex: 1400, px: 1.5, gap: 0.5 }}
            onClick={() => setGuestDrawerOpen(true)}>
            <PeopleIcon sx={{ fontSize: 18 }} /> Vendégek
          </Fab>
          <Drawer anchor="bottom" open={guestDrawerOpen} onClose={() => setGuestDrawerOpen(false)}
            PaperProps={{ sx: { maxHeight: '75vh', borderRadius: '16px 16px 0 0', overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}>
            <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'divider', mx: 'auto', mt: 1, mb: 0.5, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
              {guestPanel}
            </Box>
          </Drawer>
        </>,
        document.body
      )}

      <TableDetailDialog
        open={!!tableDialogTableId}
        onClose={() => setTableDialogTableId(null)}
        container={effectiveFullscreen ? containerRef.current : undefined}
        dbTable={tableDialogDbTableEnriched}
        unassignedGuests={allUnassigned}
        activeFilter={state.activeFilter}
        onAssignToSeat={async (guestId, tableId, seatNumber) => {
          if (String(guestId).startsWith('companion-')) {
            state.assignCompanionToSeat(guestId, tableId, seatNumber);
          } else {
            await state.assignGuestToSeat(guestId, tableId, seatNumber);
          }
        }}
        onUnassign={async (guestId) => {
          if (String(guestId).startsWith('companion-')) {
            state.unassignCompanion(guestId);
          } else {
            await state.unassignGuest(guestId);
          }
        }}
        onRename={state.renameTable}
        onDelete={(id) => handleDeleteRequest(id, true)}
        onUpdateCapacity={state.updateTableCapacity}
      />

      {/* Delete element confirmation dialog. A `container` fullscreen alatt a fullscreen
          konténerre mutat: alapértelmezetten a MUI Dialog a document.body alá portálódik,
          ami a valós Fullscreen API elemén (containerRef.current) KÍVÜLRE kerülne — emiatt
          több böngésző (főleg mobilon) automatikusan kilépett a fullscreenből, amint a
          dialógus fókuszt kapott. */}
      <Dialog
        open={deleteConfirmState.open}
        onClose={() => setDeleteConfirmState(s => ({ ...s, open: false }))}
        maxWidth="xs"
        fullWidth
        container={effectiveFullscreen ? containerRef.current : undefined}
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader icon={<DeleteIcon />} title="Törlés megerősítése" />
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            Biztosan törölni szeretnéd: <strong>{deleteConfirmState.label}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setDeleteConfirmState(s => ({ ...s, open: false }))}>
            Mégsem
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              state.deleteElement(deleteConfirmState.id, deleteConfirmState.isTable);
              if (deleteConfirmState.isTable) setTableDialogTableId(null);
              setSelectedElementId(null);
              setDeleteConfirmState({ open: false, id: null, isTable: false, label: '' });
            }}
          >
            Törlés
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation dialog for "start over" — lásd a törlés-dialógus komment a container prop-ról */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        container={effectiveFullscreen ? containerRef.current : undefined}
      >
        <DialogTitle sx={{ p: 0 }}>
          <DialogHeader icon={<RestartAltIcon />} title="Előlről kezdés" />
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            Biztosan törölsz minden asztalt, széket és elrendezést? Ez a művelet nem vonható vissza.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, gap: 1, borderTop: '1px solid', borderTopColor: 'divider' }}>
          <Button onClick={() => setResetDialogOpen(false)} disabled={resetting}>
            Mégsem
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={resetting}
            onClick={async () => {
              setResetting(true);
              try {
                await state.resetAll();
              } finally {
                setResetting(false);
                setResetDialogOpen(false);
              }
            }}
          >
            {resetting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Igen, törlöm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print-only: SVG floor plan + table-by-table guest list */}
      <div className="seating-print-view" style={{ display: 'none', fontFamily: 'sans-serif' }}>
        {/* First page: title + floor plan — must fit on exactly one page */}
        <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', pageBreakAfter: 'always', breakAfter: 'page' }}>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, color: '#2a5d32', flexShrink: 0 }}>
            Ültetési rend{event?.name ? ` – ${event.name}` : ''}
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <PrintFloorPlan layout={state.layout} seatingChart={enrichedSeatingChart} />
          </div>
        </div>

        {/* Table-by-table guest list on new page */}
        <div style={{ pageBreakBefore: 'always', paddingTop: 8 }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: '#2a5d32' }}>
            Vendégelhelyezés asztalonként
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 }}>
            {(enrichedSeatingChart.tables ?? []).map(table => (
              <div key={table.id} style={{ border: '1px solid #a5d6a7', borderRadius: 6, padding: 10, breakInside: 'avoid' }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#2a5d32', marginBottom: 4 }}>
                  {table.tableName}
                  <span style={{ fontWeight: 400, fontSize: 11, color: '#888', marginLeft: 6 }}>
                    ({table.guests?.length ?? 0}/{table.capacity})
                  </span>
                </div>
                {(table.guests ?? []).length === 0 ? (
                  <div style={{ fontSize: 11, color: '#9e9e9e' }}>Üres</div>
                ) : (
                  [...(table.guests ?? [])]
                    .sort((a, b) => (a.seatNumber ?? 99) - (b.seatNumber ?? 99))
                    .map(g => (
                      <div key={g.id} style={{ fontSize: 11, lineHeight: '1.7', color: '#212121' }}>
                        {g.seatNumber ? `${g.seatNumber}.\u00a0` : '\u2013\u00a0'}
                        {g.lastName} {g.firstName}
                        {g.isCompanion && <span style={{ fontSize: 9, color: '#1565c0' }}> [K]</span>}
                        <span style={{ fontSize: 10, color: '#e65100' }}>{getRsvpSuffix(g.rsvpStatus)}</span>
                      </div>
                    ))
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          body.seating-print-mode > *:not(.seating-print-active) {
            display: none !important;
          }
          body.seating-print-mode > .seating-print-active {
            display: block !important;
          }
          body.seating-print-mode { margin: 0; padding: 0; }
          @page { size: A4 landscape; margin: 15mm; }
        }
      `}</style>
    </>
  );
}

// ─── Print-only SVG floor plan ─────────────────────────────────────────────────

function PrintFloorPlan({ layout, seatingChart }) {
  const { width, height } = layout.venue;
  const tables = seatingChart?.tables ?? [];

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      style={{ width: '100%', height: '100%', display: 'block', border: '1px solid #ccc', borderRadius: 4, background: '#f0ede6' }}
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Grid pattern */}
      <defs>
        <pattern id="pg" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(0,0,0,0.07)" strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width={width} height={height} fill="url(#pg)" />

      {/* Venue polygon border */}
      {layout.venue.polygon && (
        <polygon
          points={layout.venue.polygon.map(p => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke="#2a5d32"
          strokeWidth={3}
          strokeDasharray="10 5"
        />
      )}

      {/* Non-table elements */}
      {(layout.elements ?? []).map(el => {
        const style_ = MODULE_STYLES[el.type] || { bgcolor: '#e0e0e0' };
        const cx = el.x + el.width / 2;
        const cy = el.y + el.height / 2;
        return (
          <g key={el.id} transform={el.rotation ? `rotate(${el.rotation},${cx},${cy})` : undefined}>
            <rect x={el.x} y={el.y} width={el.width} height={el.height} rx={6}
              fill={style_.bgcolor} stroke="#9e9e9e" strokeWidth={1.5} />
            <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
              fontSize={Math.min(14, el.height * 0.28)} fill="#424242" fontFamily="sans-serif">
              {el.label}
            </text>
          </g>
        );
      })}

      {/* Tables */}
      {Object.entries(layout.tables ?? {}).map(([tableId, td]) => {
        const dbTable = tables.find(t => t.id === parseInt(tableId, 10));
        const isRound = td.shape === 'round';
        const cx = td.x + td.width / 2;
        const cy = td.y + td.height / 2;
        const guestCount = dbTable?.guests?.length ?? 0;
        const capacity = dbTable?.capacity ?? 0;
        return (
          <g key={tableId} transform={td.rotation ? `rotate(${td.rotation},${cx},${cy})` : undefined}>
            {isRound ? (
              <ellipse cx={cx} cy={cy} rx={td.width / 2} ry={td.height / 2}
                fill="#e8f5e9" stroke="#a5d6a7" strokeWidth={1.5} />
            ) : (
              <rect x={td.x} y={td.y} width={td.width} height={td.height} rx={6}
                fill="#e8f5e9" stroke="#a5d6a7" strokeWidth={1.5} />
            )}
            <text x={cx} y={cy - 7} textAnchor="middle" dominantBaseline="middle"
              fontSize={Math.min(14, td.height * 0.22)} fontWeight="bold" fill="#2a5d32" fontFamily="sans-serif">
              {dbTable?.tableName || `#${tableId}`}
            </text>
            <text x={cx} y={cy + 9} textAnchor="middle" dominantBaseline="middle"
              fontSize={Math.min(11, td.height * 0.17)} fill="#4caf50" fontFamily="sans-serif">
              {guestCount}/{capacity}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
